import React, { useState } from "react";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { errMsg, fmt, useApi, useLoad } from "../../lib/api";
import { Empty, ErrorBox, Loading, StatusBadge } from "../../components/kit";

function usePay() {
  const api = useApi();
  const [busy, setBusy] = useState("");
  const pay = async (product: string, ad_id?: string) => {
    if (busy) return;
    setBusy(product + (ad_id || ""));
    try {
      const r = await api("post", "/vendor/billing/checkout", { product, ad_id, origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  return { busy, pay };
}

function Plans({ d, pay, busy }: { d: any; pay: (p: string) => void; busy: string }) {
  const tier = d.subscription?.tier;
  return (
    <div className="card space-y-3" data-testid="billing-plans">
      <h3 className="text-2xl font-bold">Plan</h3>
      <p className="text-sm">Current: <strong data-testid="billing-current-tier">{tier}</strong> · <StatusBadge value={d.subscription?.status} testId="billing-current-status" />{d.subscription?.source === "stripe" && <span className="badge ml-1">paid via Stripe</span>}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {(["basic", "pro"] as const).map((k) => (
          <div key={k} className="rounded-lg border border-[var(--line)] p-4" data-testid={`billing-plan-${k}`}>
            <p className="font-bold">{d.products[k].label}</p>
            <p className="text-2xl font-extrabold text-[var(--navy)]">${d.products[k].amount.toFixed(2)}<span className="text-sm font-normal">/mo</span></p>
            {d.is_owner ? (
              <button className="btn btn-primary btn-sm mt-2 w-full" disabled={!!busy || (tier === k && d.subscription?.source === "stripe")} onClick={() => pay(k)} data-testid={`billing-subscribe-${k}`}>
                {tier === k && d.subscription?.source === "stripe" ? "Current plan" : busy === k ? "Opening checkout…" : `Choose ${k}`}
              </button>
            ) : null}
          </div>
        ))}
      </div>
      <p className="text-xs text-[var(--muted)]">Your plan never changes organic matching or ranking.</p>
    </div>
  );
}

function AdPayments({ d, pay, busy }: { d: any; pay: (p: string, ad?: string) => void; busy: string }) {
  const approved = d.ads.filter((a: any) => a.status === "approved");
  return (
    <div className="card space-y-3" data-testid="billing-ads">
      <h3 className="text-2xl font-bold">Sponsored placements</h3>
      <p className="text-sm text-[var(--muted)]">{d.products.sponsored.label}: ${d.products.sponsored.amount.toFixed(2)}, paid once. New ads run only after ops approval <em>and</em> payment.</p>
      {!approved.length && <Empty title="No approved ads yet" />}
      <ul className="space-y-2">
        {approved.map((a: any) => {
          const live = a.paid_until && new Date(a.paid_until) > new Date();
          return (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`billing-ad-${a.id}`}>
              <span className="min-w-0 break-words"><strong>{a.title}</strong> · {live ? `running until ${fmt(a.paid_until)}` : a.billing_required ? "not running (unpaid)" : "running (legacy test ad)"}</span>
              {d.is_owner && a.billing_required !== false && (
                <button className="btn btn-dark btn-sm" disabled={!!busy} onClick={() => pay("sponsored", a.id)} data-testid={`billing-pay-ad-${a.id}`}>{live ? "Extend 30 days" : "Pay to run"}</button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function VendorBilling() {
  const { data, loading, error, reload } = useLoad<any>("/vendor/billing");
  const { busy, pay } = usePay();
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="vendor-billing">
      <p className="badge badge-amber inline-flex items-center gap-1"><CreditCard className="h-4 w-4" /> {data.mode === "test" ? "Stripe test mode: use card 4242 4242 4242 4242, no real money moves" : "Live payments"}</p>
      {!data.is_owner && <p className="text-sm text-[var(--muted)]" data-testid="billing-owner-only">Only organization owners can pay or change the plan.</p>}
      <div className="grid gap-6 lg:grid-cols-2">
        <Plans d={data} pay={pay} busy={busy} />
        <AdPayments d={data} pay={pay} busy={busy} />
      </div>
      <div className="card" data-testid="billing-history">
        <h3 className="mb-2 text-xl font-bold">Payment history</h3>
        {!data.transactions.length ? <Empty title="No payments yet" /> : (
          <ul className="space-y-1 text-sm">{data.transactions.map((t: any, i: number) => (
            <li key={i} className="flex flex-wrap justify-between gap-2"><span>{t.label} · ${t.amount.toFixed(2)} · {fmt(t.created_at)}</span><StatusBadge value={t.payment_status} /></li>
          ))}</ul>
        )}
      </div>
    </div>
  );
}
